import * as React from 'react';
import { z } from 'zod';
import { Button } from './Button';
import { Surface } from './Card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { Form, FormField, type FormFieldControl, type ZodForm } from './Form';
import { FileDropField, type FileDropFieldFile } from './FileDropField';
import { Stack } from './Layout';
import { Loading } from './Spinner';
import { Text } from './Text';

const FORM_ID = 'bulk-add-rates-form';

export const bulkAddRatesSchema = z.object({
  customerId: z.string().min(1, 'Choose a customer account.'),
  /** The file's name stands for the file; the container holds the File itself. */
  fileName: z.string().min(1, 'Choose a rate document to upload.'),
});

export type BulkAddRatesValues = z.infer<typeof bulkAddRatesSchema>;
export type BulkAddRatesForm = ZodForm<typeof bulkAddRatesSchema>;

export interface BulkAddRatesDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Replaces the whole dialog body with a spinner while the company resolves. */
  loadingCompany?: boolean;
  /** From `useZodForm(bulkAddRatesSchema, …)`. */
  form: BulkAddRatesForm;
  /** A customer fixed by the page it opened from: shown as a note instead of the picker. */
  lockedCustomerName?: string | null;
  /**
   * The account picker, when no customer is locked. The container renders it:
   * it fetches. It is handed the bound `customerId` field.
   */
  customerPicker?: (
    field: FormFieldControl<typeof bulkAddRatesSchema, 'customerId'>
  ) => React.ReactNode;
  file: FileDropFieldFile | null;
  onFileChosen: (file: File) => void;
  onRemoveFile: () => void;
  submitting?: boolean;
  onSubmit: (values: BulkAddRatesValues) => void;
}

/**
 * The bulk-add-rates dialog: pick the customer account, drop a rate sheet,
 * parse it.
 *
 *   <BulkAddRatesDialogView open onOpenChange={close}
 *     customerPicker={<AccountSelector … />} file={file}
 *     onFileChosen={choose} onRemoveFile={clear}
 *     canSubmit={!!file && !!customerId} onSubmit={parse} />
 */
export function BulkAddRatesDialogView({
  open,
  onOpenChange,
  loadingCompany = false,
  form,
  lockedCustomerName = null,
  customerPicker,
  file,
  onFileChosen,
  onRemoveFile,
  submitting = false,
  onSubmit,
}: BulkAddRatesDialogViewProps) {
  if (loadingCompany) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="lg">
          <Loading size="xl" pad="sm" title="Loading company information..." />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Bulk Add Rates from File</DialogTitle>
        </DialogHeader>

        <Form form={form} gap={6} id={FORM_ID} onSubmit={onSubmit}>
          <FormField
            form={form}
            name="customerId"
            label={lockedCustomerName ? 'Customer Account' : undefined}
          >
            {(field) =>
              lockedCustomerName ? (
                <Surface radius="md" padding="sm" tone="muted">
                  <Stack gap={0}>
                    <Text variant="body-strong">{lockedCustomerName}</Text>
                    <Text variant="body-sm" tone="muted">
                      Account locked for this upload
                    </Text>
                  </Stack>
                </Surface>
              ) : (
                customerPicker?.(field)
              )
            }
          </FormField>

          {/* The drop zone reports the chosen file's name into the form, so
              a submit with nothing dropped is refused like any other field. */}
          <FileDropField
            label="Rate Document"
            form={form}
            name="fileName"
            file={file}
            accept=".pdf,application/pdf,.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
            title="Upload Rate Document"
            description="Drag and drop your PDF or image file here, or click to browse"
            footnote="Supports PDF files and images (PNG, JPEG, WebP) up to 10MB"
            /*
             * The name is recorded without re-validating: the container may
             * reject the file it was handed — wrong kind, too big — and set
             * its own message, which an async revalidation would clear out
             * from under it. The schema has its say on submit.
             */
            onFileChosen={(chosen) => {
              form.setValue('fileName', chosen.name);
              onFileChosen(chosen);
            }}
            onRemoveFile={() => {
              form.setValue('fileName', '');
              onRemoveFile();
            }}
          />
        </Form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            form={FORM_ID}
            type="submit"
            disabled={submitting}
            loading={submitting}
          >
            {submitting ? 'Parsing File...' : 'Parse and Add Rates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
